import { DillDropdown } from '../../../../../../general/Dill';

interface SupplierUserBranchAccessSectionProps {
  branchOptions: string[];
  isLoading?: boolean;
  branches: string[];
  onBranchesChange: (branches: string[]) => void;
}

/**
 * Assigns a supplier user to branches for AR / payment data. Leaving it empty is meaningful — an
 * unassigned user sees every branch's payment requests, which is the default for existing users.
 *
 * Branch values are the raw `branch` strings carried by the supplier's customers, derived via
 * `useSupplierBranchOptions`, not ids from the hand-entered `SupplierAdditionalDetails.branches`.
 */
export const SupplierUserBranchAccessSection = ({
  branchOptions,
  isLoading = false,
  branches,
  onBranchesChange,
}: SupplierUserBranchAccessSectionProps) => (
  // shrink-0 matters: the modal body is a `flex flex-col overflow-y-auto`, so without it this
  // card is shrunk by flexbox and its `overflow-hidden` clips the contents away entirely.
  <div className="border border-gray-200 rounded-lg overflow-hidden shrink-0">
    <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
        Branch Access
      </span>
    </div>
    <div className="px-3 py-3">
      {branchOptions.length === 0 && !isLoading ? (
        <p className="text-[11px] text-gray-400 m-0">
          None of this supplier's customers have a branch set, so there is nothing to scope by yet.
        </p>
      ) : (
        <>
          <DillDropdown
            isMulti
            isSearchable
            closeMenu={false}
            showSelectedItems
            isLoading={isLoading}
            placeholder={isLoading ? 'Loading branches...' : 'All branches'}
            value={null}
            multiValue={branches}
            items={branchOptions.map((branch) => ({ label: branch, value: branch }))}
            onMultiSelectChange={(selected) => {
              onBranchesChange((selected ?? []).map((item) => `${item.value}`));
            }}
          />
          <p className="text-[10px] text-gray-400 mt-1.5 mb-0">
            {branches.length === 0
              ? 'Unassigned — this user can see payment requests for all branches.'
              : `Limited to ${branches.length} branch${
                  branches.length === 1 ? '' : 'es'
                }. This user only sees payment requests for customers in ${
                  branches.length === 1 ? 'that branch' : 'those branches'
                }.`}
          </p>
        </>
      )}
    </div>
  </div>
);
